
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import SEOLayout from './layouts/SEOLayout';
import { HelmetProvider } from 'react-helmet-async';
import { SEOProvider } from './context/SEOContext';
import { ToastProvider } from './components/ToastContainer';
import useAuthStore from './stores/useAuthStore';
import { initializeSocket, disconnectSocket } from './utils/socket';

import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import GlobalLoader from './components/GlobalLoader';
import AdminCourseCreatePage from './pages/AdminCourseCreatePage';
import InstructorDiscussionDashboard from './pages/InstructorDiscussionDashboard';
import InstructorCertificateDashboard from './pages/InstructorCertificateDashboard';
import AdminCertificateManagement from './pages/AdminCertificateManagement';
import StudentBillingPage from './pages/StudentBillingPage';
import StudentProfilePage from './pages/StudentProfilePage';
import InstructorProfilePage from './pages/InstructorProfilePage';
import StudentNotificationPage from './pages/StudentNotificationPage';
import InstructorNotificationPage from './pages/InstructorNotificationPage';
import AdminNotificationPage from './pages/InstructorNotificationPage';
import InstructorPayoutSettingsPage from './pages/InstructorPayoutSettingsPage';
import AdminAssignmentCreatePage from './pages/AdminAssigmentCreatePage';
import AdminQuizCreatePage from './pages/AdminQuizCreatePage';
import VerifyCertificatePage from './pages/VerifyCertificatePage';
import CertificateVerifyPage from './pages/CertificateVerifyPage';
import InstructorPartnerDashboard from './pages/InstructorPartnerDashboard';
import AdminPartnerDashboard from './pages/AdminPartnerDashboard';
import HighSchoolLandingPage from './pages/HighSchoolLandingPage';
import OrganizationLandingPage from './pages/OrganizationLandingPage';
import AdminOrganizationManagement from './pages/AdminOrganizationManagement';
import AdminAnalyticsDashboard from './pages/AdminAnalyticsDashboard';
import AdminSchoolManagement from './pages/AdminSchoolManagement';


import AdminWithdrawalPage from './pages/AdminWithdrawalPage';
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CourseCatalog from './pages/CourseCatalog';
import CategoryLandingPage from './pages/CategoryLandingPage';
import CategoryCoursePage from './pages/CategoryCoursePage';
import CourseDetails from './pages/CourseDetails';
import CertificatesPage from './pages/CertificatesPage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import HelpSupportPage from './pages/HelpSupportPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DiscussionPage from './pages/DiscussionPage';
import LessonPage from './pages/LessonPage';
import LearningDashboardPage from './pages/LearningDashboardPage';
import QuizPage from './pages/QuizPage';
import BadgesPage from './pages/BadgesPage';
import MyCourses from './pages/MyCourses';
import AssignmentPage from './pages/AssignmentPage';
import EditProfile from './pages/EditProfile';
import StudentDashboard from './pages/StudentDashboard';
import StudentOnboardingPage from './pages/StudentOnboardingPage';
import WishlistPage from './pages/WishlistPage';
import PaymentVerification from './pages/PaymentVerification';
import InstructorDashboard from './pages/InstructorDashboard';
import InstructorCourseDashboard from './pages/InstructorCourseDashboard';
import InstructorOnboardingPage from './pages/InstructorOnboardingPage';
import InstructorPendingVerificationPage from './pages/InstructorPendingVerificationPage';
import InstructorAssignmentDashboard from './pages/InstructorAssignmentDashboard';
import GradingPage from './pages/GradingPage';
import InstructorAssignmentCreatePage from './pages/InstructorAssignmentCreatePage';
import InstructorAnalyticsPage from './pages/InstructorAnalyticsPage';
import InstructorRevenuePage from './pages/InstructorRevenuePage';
import InstructorMediaLibrary from './pages/InstructorMediaLibrary';
import InstructorCourseCreatePage from './pages/InstructorCourseCreatePage';
import InstructorQuizCreatePage from './pages/InstructorQuizCreatePage';
import StudentProgressPage from './pages/StudentProgressPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminUserManagement from './pages/AdminUserManagement';
import AdminCourseManagement from './pages/AdminCourseManagement';
import AdminCategoryManagement from './pages/AdminCategoryManagement';
import AdminCouponManagement from './pages/AdminCouponManagement';
import InstructorCouponPage from './pages/InstructorCouponPage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import NotFoundPage from './pages/NotFoundPage';


function App() {
  const { token, user, fetchProfile } = useAuthStore();

  useEffect(() => {
    if (token) {
      initializeSocket(token);
      fetchProfile();
    }

    return () => {
      disconnectSocket();
    };
  }, [token, fetchProfile]);

  const ProtectedRoute = ({ children, allowedRoles = [] }) => {

    if (!token) {
      return <Navigate to="/login" />;
    }

    if (user && user.role === 'instructor' && !user.onboarding?.completed) {
      return <Navigate to="/instructor/onboarding" />;
    }

    if (user && user.role === 'instructor' && user.onboarding?.completed && !user.onboarding?.adminVerified) {
      return <Navigate to="/instructor/pending-verification" />;
    }

    if (user && user.role === 'student' && !user.onboarding?.completed) {
      return <Navigate to="/student/onboarding" />;
    }

    if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
      return <Navigate to="/" />;
    }

    return children;
  };

  return (
    <ToastProvider position="top-right" maxToasts={3}>
      <HelmetProvider>
        <SEOProvider>
          <Router>
            <ScrollToTop />
            <GlobalLoader />
              <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
                <SEOLayout>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/change-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/courses" element={<CourseCatalog />} />
                    <Route path="/categories" element={<CategoryLandingPage />} />
                    <Route path="/categories/:categorySlug" element={<CategoryCoursePage />} />
                    <Route path="/course/:slug/:id" element={<CourseDetails />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/help-support" element={<HelpSupportPage />} />
                    <Route path="/terms" element={<TermsAndConditionsPage />} />
                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/verify" element={<VerifyCertificatePage />} />
                    <Route path="/verify/:certificateId" element={<CertificateVerifyPage />} />
                    <Route path="/school/highschool" element={<HighSchoolLandingPage />} />
                    <Route path="/org/company" element={<OrganizationLandingPage />} />


                    {/* Student Routes */}
                    <Route path='/student/notifications'
                      element={
                        <ProtectedRoute allowedRoles={['student']}>
                          <StudentNotificationPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/student/discussion/:id'
                      element={
                        <ProtectedRoute allowedRoles={['student']}>
                          <DiscussionPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/student/learning/:courseId' element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <LearningDashboardPage />
                      </ProtectedRoute>
                    } />
                    <Route path='/student/learning/discussions/:courseId' element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <DiscussionPage />
                      </ProtectedRoute>
                    } />
                    <Route path='/student/learning/:courseId/lesson/:lessonId' element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <LessonPage />
                      </ProtectedRoute>
                    } />
                    <Route path='/student/quiz/:courseId/:quizId' element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <QuizPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/student/achivements"
                      element={
                        <ProtectedRoute allowedRoles={['student']}>
                          <BadgesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/student/my-courses'
                      element={
                        <ProtectedRoute allowedRoles={['student']}>
                          <MyCourses />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/student/:courseId/assignment/:assignmentId' element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <AssignmentPage />
                      </ProtectedRoute>
                    } />
                    <Route path='/student/edit-profile'
                      element={
                        <ProtectedRoute allowedRoles={['student']}>
                          <EditProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/student/dashboard"
                      element={
                        <ProtectedRoute allowedRoles={['student']}>
                          <StudentDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="student/onboarding" element={<StudentOnboardingPage />} />
                    <Route path="/student/wishlist"
                      element={
                        <ProtectedRoute allowedRoles={['student']}>
                          <WishlistPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/student/billings"
                      element={
                        <ProtectedRoute allowedRoles={['student']}>
                          <StudentBillingPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route path="/student/progress" element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <StudentProgressPage />
                      </ProtectedRoute>
                    } />

                    <Route path="/student/course/:courseId/certificate" element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <CertificatesPage />
                      </ProtectedRoute>
                    } />

                    <Route path="/student/profile" element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <StudentProfilePage />
                      </ProtectedRoute>
                    } />


                    {/* Instructor Routes */}
                    <Route path='/instructor/notifications'
                      element={
                        <ProtectedRoute allowedRoles={['instructor']}>
                          <InstructorNotificationPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/instructor/certificates'
                      element={
                        <ProtectedRoute allowedRoles={['instructor']}>
                          <InstructorCertificateDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/instructor/partnerprogram'
                      element={
                        <ProtectedRoute allowedRoles={['instructor']}>
                          <InstructorPartnerDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/instructor/discussions'
                      element={
                        <ProtectedRoute allowedRoles={['instructor']}>
                          <InstructorDiscussionDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/instructor/edit-profile'
                      element={
                        <ProtectedRoute allowedRoles={['instructor']}>
                          <EditProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/instructor/dashboard'
                      element={
                        <ProtectedRoute allowedRoles={['instructor']}>
                          <InstructorDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/instructor/coupons'
                      element={
                        <ProtectedRoute allowedRoles={['instructor']}>
                          <InstructorCouponPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/instructor/my-courses'
                      element={
                        <ProtectedRoute allowedRoles={['instructor']}>
                          <InstructorCourseDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/instructor/onboarding' element={<InstructorOnboardingPage />} />
                    <Route path='/instructor/pending-verification' element={<InstructorPendingVerificationPage />} />
                    <Route path='/instructor/assignment' element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <InstructorAssignmentDashboard />
                      </ProtectedRoute>
                    } />
                    <Route path='/instructor/grade/:submissionId' element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <GradingPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/instructor/create-assignment/:lessonId" element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <InstructorAssignmentCreatePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/instructor/edit-assignment/:lessonId/:assignmentId" element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <InstructorAssignmentCreatePage />
                      </ProtectedRoute>
                    } />
                    <Route path='/instructor/analytics' element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <InstructorAnalyticsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="instructor/revenue" element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <InstructorRevenuePage />
                      </ProtectedRoute>
                    } />
                    <Route path='/instructor/media/library' element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <InstructorMediaLibrary />
                      </ProtectedRoute>
                    } />
                    <Route path='/instructor/course/builder' element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <InstructorCourseCreatePage />
                      </ProtectedRoute>
                    } />
                    <Route path='/instructor/course/edit-course/:id' element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <InstructorCourseCreatePage />
                      </ProtectedRoute>
                    } />
                    <Route path='/instructor/create-quiz/:lessonId' element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <InstructorQuizCreatePage />
                      </ProtectedRoute>
                    } />
                    <Route path='/instructor/edit-quiz/:assignmentId/:quizId' element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <InstructorQuizCreatePage />
                      </ProtectedRoute>
                    } />
                    <Route path='/instructor/profile' element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <InstructorProfilePage />
                      </ProtectedRoute>
                    } />
                    <Route path='/instructor/revenue/withdrawal/payout-settings' element={
                      <ProtectedRoute allowedRoles={['instructor']}>
                        <InstructorPayoutSettingsPage />
                      </ProtectedRoute>
                    } />


                    {/* Admin Routes */}
                    <Route path='/admin/cloud/dashboard'
                      element={
                        <ProtectedRoute allowedRoles={['admin']}>
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/admin/cloud/users'
                      element={
                        <ProtectedRoute allowedRoles={['admin']}>
                          <AdminUserManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/admin/cloud/courses'
                      element={
                        <ProtectedRoute allowedRoles={['admin']}>
                          <AdminCourseManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/admin/cloud/certificates'
                      element={
                        <ProtectedRoute allowedRoles={['admin']}>
                          <AdminCertificateManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/admin/cloud/organizations'
                      element={
                        <ProtectedRoute allowedRoles={['admin']}>
                          <AdminOrganizationManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/cloud/analytics/:entityType/:entityId"
                      element={
                        <ProtectedRoute allowedRoles={['admin']}>
                          <AdminAnalyticsDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/admin/cloud/schools" element={<ProtectedRoute allowedRoles={['admin']}><AdminSchoolManagement /></ProtectedRoute>} />
                    <Route path='/admin/cloud/course/builder'
                      element={
                        <ProtectedRoute allowedRoles={['admin']}>
                          <AdminCourseCreatePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/admin/cloud/course/edit-course/:id' element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminCourseCreatePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/cloud/create-assignment/:lessonId" element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminAssignmentCreatePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/cloud/edit-assignment/:lessonId/:assignmentId" element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminAssignmentCreatePage />
                      </ProtectedRoute>
                    } />
                    <Route path='/admin/cloud/create-quiz/:lessonId' element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminQuizCreatePage />
                      </ProtectedRoute>
                    } />
                    <Route path='/admin/cloud/edit-quiz/:assignmentId/:quizId' element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminQuizCreatePage />
                      </ProtectedRoute>
                    } />
                    <Route path='/admin/cloud/categories'
                      element={
                        <ProtectedRoute allowedRoles={['admin']}>
                          <AdminCategoryManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/admin/cloud/partners'
                      element={
                        <ProtectedRoute allowedRoles={['admin']}>
                          <AdminPartnerDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/admin/cloud/withdrawal'
                      element={
                        <ProtectedRoute allowedRoles={['admin']}>
                          <AdminWithdrawalPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path='/admin/cloud/analytics' element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminAnalyticsPage />
                      </ProtectedRoute>
                    } />
                    <Route path='/admin/cloud/coupons' element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminCouponManagement />
                      </ProtectedRoute>
                    } />
                    <Route path='/admin/cloud/notifications' element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminNotificationPage />
                      </ProtectedRoute>
                    } />
                    <Route path='/admin/cloud/settings' element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminSettingsPage />
                      </ProtectedRoute>
                    } />

                    {/* Payment verification */}
                    <Route path="/payment-verification" element={<PaymentVerification />} />

                    {/* Catch-all for 404 */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </SEOLayout>
                <Footer />
                <Toaster />
              </div>
          </Router>
        </SEOProvider>
      </HelmetProvider>
    </ToastProvider>
  );
}

export default App;